import { FieldValues, useFormContext } from "react-hook-form";
import { useCallback, useMemo } from "react";
import { addMinutes, getDay } from "date-fns";
import { PlanningConfig } from "../types";
import { PlanningModeEnum } from "shared/enums/globalEnums";
import { planningConfigurations } from "../config/planningConfigurations";

interface UsePlanningItemProps {
  config: PlanningConfig;
  index: number;
  initialData: FieldValues;
}

export const usePlanningItem = ({ config, index, initialData }: UsePlanningItemProps) => {
  const { setValue, getValues } = useFormContext();
  const currentPlanning = getValues(config.name);
  const currentActive = currentPlanning.active;
  const isOverlappingDays = getDay(currentPlanning.start_date) !== getDay(currentPlanning.end_date);
  const isSocManagement = config.mode === PlanningModeEnum.soc_management;

  const getPreviousActivePlannning = useCallback(() => {
    let prevIndex = index - 1;
    while (prevIndex >= 0) {
      const prevConfig = planningConfigurations[prevIndex];
      const prevActive = getValues(`${prevConfig.name}.active`);
      if (prevActive) {
        return getValues(prevConfig.name);
      }
      prevIndex--;
    }
    return null;
  }, [index, getValues]);

  const prevPlanningValues = useMemo(() => getPreviousActivePlannning(), [getPreviousActivePlannning]);

  const validateStartDate = useCallback(
    (value: Date | null) => {
      if (!value) return "Start date is required";
      const minAllowed = prevPlanningValues ? new Date(prevPlanningValues.end_date) : new Date();
      if (value < minAllowed) {
        return prevPlanningValues
          ? "Start date cannot be before the previous planning's end date"
          : "Start date cannot be in the past";
      }
      return undefined;
    },
    [prevPlanningValues],
  );

  const validateEndDate = useCallback(
    (value: Date | null) => {
      if (!value) return "End date is required";
      if (value <= currentPlanning.start_date) {
        return "End date cannot be before or equal to the start date";
      }
      return undefined;
    },
    [currentPlanning.start_date],
  );

  const recalcFollowingPlannings = useCallback(
    (changedIndex: number, newStart: Date) => {
      let currentStart = newStart;
      planningConfigurations.slice(changedIndex + 1).map((nextConfig) => {
        const active = getValues(`${nextConfig.name}.active`);
        if (active) {
          setValue(`${nextConfig.name}.start_date`, currentStart, { shouldDirty: true });
          const newEnd = addMinutes(currentStart, nextConfig.durationInMinutes);
          setValue(`${nextConfig.name}.end_date`, newEnd, { shouldDirty: true });
          currentStart = newEnd;
        }
      });
    },
    [getValues, setValue],
  );

  const handleStartDateChange = useCallback(
    (newDate: Date | null) => {
      if (!newDate) return;
      setValue(`${config.name}.start_date`, newDate, { shouldDirty: true });
      const newEnd = addMinutes(newDate, config.durationInMinutes);
      setValue(`${config.name}.end_date`, newEnd, { shouldDirty: true });
      recalcFollowingPlannings(index, newEnd);
    },
    [config.name, config.durationInMinutes, index, recalcFollowingPlannings, setValue],
  );

  const handleEndDateChange = useCallback(
    (newDate: Date | null) => {
      if (!newDate) return;
      setValue(`${config.name}.end_date`, newDate, { shouldDirty: true });
      recalcFollowingPlannings(index, newDate);
    },
    [config.name, index, recalcFollowingPlannings, setValue],
  );

  const handleActiveToggle = useCallback(
    (newActive: boolean) => {
      setValue(`${config.name}.active`, newActive, { shouldDirty: true });

      if (newActive) {
        let newStart: Date;
        if (prevPlanningValues) {
          newStart = prevPlanningValues.end_date;
        } else {
          newStart = initialData.startingDate;
        }
        setValue(`${config.name}.start_date`, newStart, { shouldDirty: true });
        const newEnd = addMinutes(newStart, config.durationInMinutes);
        setValue(`${config.name}.end_date`, newEnd, { shouldDirty: true });
        recalcFollowingPlannings(index, newEnd);
      } else {
        let newStart: Date;
        if (prevPlanningValues) {
          newStart = new Date(prevPlanningValues.end_date);
        } else {
          newStart = new Date(initialData.startingDate);
        }
        recalcFollowingPlannings(index - 1, newStart);
      }
    },
    [config.name, config.durationInMinutes, index, prevPlanningValues, recalcFollowingPlannings, setValue, initialData],
  );

  return {
    currentActive,
    isOverlappingDays,
    isSocManagement,
    validateStartDate,
    validateEndDate,
    handleStartDateChange,
    handleEndDateChange,
    handleActiveToggle,
    prevPlanningValues,
    currentPlanning,
  };
};
